import { useState, type MouseEvent, type ReactNode } from 'react';
import { Badge, Button, Modal, ValuePills } from '@/components/ui';
import {
  PRODUCTS_PURCHASED_LABEL,
  PROJECTS_PARTICIPATED_LABEL,
  purchaseCycleLabel,
  purchasedItemsOf,
  purchaseTitle,
} from '@/lib/company-purchases';
import { orDash } from '@/lib/format';
import type { CompanyPurchaseCycle, CompanyPurchaseRef } from '@/lib/types';

/**
 * The Products purchased / Projects participated in cell, shared by `/companies`
 * and the UP dashboard's Companies card.
 *
 * Owner's decisions (2026-10-09): the cell shows ONLY the HIGHLIGHTED cycle's
 * items as coloured pills, then one "View more (N)" button opening a modal that
 * lists every cycle. Highlighted = the cycles the column's cycle filter selected,
 * or — with no filter — the LATEST cycle (buckets arrive newest first, so the
 * first bucket carrying a cycle; failing that, the first bucket).
 *
 * Three states, kept apart and never coalesced (see `company-purchases.ts`):
 * - `null` / `undefined` — not reported. A muted "Not available" with a `title`
 *   saying so; ⛔ never a dash, which would read as *bought nothing*.
 * - nothing of this kind — the em dash.
 * - items — highlighted pills (the `info` Badge tone) and the button.
 *
 * ⚠️ Colour is never the only signal: the highlighted group carries the cycle
 * name(s) as its accessible label and `title`.
 *
 * ⚠️ The button and the modal stop click propagation: the modal portals to the
 * body, but React bubbles its events through the component tree, so without the
 * wrapper a click inside the dialog would reach a clickable table row.
 */
export interface PurchaseHistoryCellProps {
  /** row.purchaseHistory as served: buckets newest first. null/undefined = not read (render as the existing renderers render it), [] = nothing bought. */
  history: readonly CompanyPurchaseCycle[] | null | undefined;
  kind: 'products' | 'projects';
  /** Cycle ids the column's cycle filter has selected. [] = no filter. */
  highlightCycleIds: readonly number[];
  /** For the modal title, e.g. "<companyName> — projects participated in". */
  companyName: string;
}

function itemName(item: CompanyPurchaseRef): string {
  const trimmed = item.name.trim();
  return trimmed === '' ? `#${item.id}` : trimmed;
}

/** The buckets highlighted for this row — see the component doc. */
function highlightedBuckets(
  history: readonly CompanyPurchaseCycle[],
  highlightCycleIds: readonly number[],
): Set<CompanyPurchaseCycle> {
  if (highlightCycleIds.length > 0) {
    const wanted = new Set(highlightCycleIds);
    return new Set(history.filter((b) => b.cycle !== null && wanted.has(b.cycle.id)));
  }
  const latest = history.find((b) => b.cycle !== null) ?? history[0];
  return new Set(latest ? [latest] : []);
}

function stop(event: MouseEvent) {
  event.stopPropagation();
}

function HighlightedPills({
  items,
  cycleLabel,
}: {
  items: readonly CompanyPurchaseRef[];
  cycleLabel: string;
}) {
  return (
    <span
      role="group"
      aria-label={cycleLabel}
      title={cycleLabel}
      className="flex min-w-0 flex-wrap gap-1"
    >
      {items.map((item) => (
        <Badge key={item.id} tone="info" className="max-w-full">
          {/* Badge is nowrap; the inner span lets one long name wrap in a narrow cell. */}
          <span className="min-w-0 break-words whitespace-normal">{itemName(item)}</span>
        </Badge>
      ))}
    </span>
  );
}

export function PurchaseHistoryCell({
  history,
  kind,
  highlightCycleIds,
  companyName,
}: PurchaseHistoryCellProps): ReactNode {
  const [open, setOpen] = useState(false);
  const read = history ?? null;
  const all = purchasedItemsOf(read, kind);

  if (read === null || all === null) {
    return (
      <span
        className="text-xs text-fg-subtle"
        title={
          kind === 'products'
            ? 'Purchased products were not reported for this account.'
            : 'Participating projects were not reported for this account.'
        }
      >
        Not available
      </span>
    );
  }
  if (all.length === 0) {
    return <span className="text-fg-subtle">{orDash(null)}</span>;
  }

  const highlighted = highlightedBuckets(read, highlightCycleIds);
  const shown: CompanyPurchaseRef[] = [];
  const seen = new Set<number>();
  for (const bucket of read) {
    if (!highlighted.has(bucket)) continue;
    for (const item of bucket[kind]) {
      if (seen.has(item.id)) continue;
      seen.add(item.id);
      shown.push(item);
    }
  }
  const remaining = read
    .filter((bucket) => !highlighted.has(bucket))
    .reduce((sum, bucket) => sum + bucket[kind].length, 0);
  const highlightedLabel = read
    .filter((bucket) => highlighted.has(bucket))
    .map((bucket) => purchaseCycleLabel(bucket.cycle))
    .join(', ');
  const kindLabel = kind === 'products' ? PRODUCTS_PURCHASED_LABEL : PROJECTS_PARTICIPATED_LABEL;
  const title = purchaseTitle(read, kind);

  return (
    <span className="flex min-w-0 flex-col items-start gap-1" title={title}>
      {shown.length > 0 && <HighlightedPills items={shown} cycleLabel={highlightedLabel} />}
      {remaining > 0 && (
        <Button
          type="button"
          size="sm"
          variant="ghost"
          onClick={(event) => {
            event.stopPropagation();
            setOpen(true);
          }}
        >
          View more ({remaining})
        </Button>
      )}
      <span onClick={stop} className="contents">
        <Modal
          open={open}
          onClose={() => setOpen(false)}
          title={`${companyName} — ${kindLabel.toLowerCase()}`}
          size="md"
        >
          <ul className="m-0 list-none divide-y divide-border p-0">
            {read.map((bucket) => {
              const label = purchaseCycleLabel(bucket.cycle);
              const items = bucket[kind];
              return (
                <li
                  key={bucket.cycle ? `cycle:${bucket.cycle.id}` : 'cycle:none'}
                  className="py-2.5"
                >
                  <h3
                    className={
                      bucket.cycle
                        ? 'm-0 mb-1.5 text-sm font-medium text-fg'
                        : 'm-0 mb-1.5 text-sm text-fg-muted'
                    }
                  >
                    {label}
                  </h3>
                  {highlighted.has(bucket) && items.length > 0 ? (
                    <HighlightedPills items={items} cycleLabel={label} />
                  ) : (
                    <ValuePills
                      items={items.map((item) => ({ key: item.id, label: itemName(item) }))}
                    />
                  )}
                </li>
              );
            })}
          </ul>
        </Modal>
      </span>
    </span>
  );
}
